import { all, call, put, takeLatest } from "redux-saga/effects";
import { adminAPI, authAPI } from "../api/api";
import { adminActionTypes, adminActions } from "../store/reducers/adminReducer";
import { generalActions } from "../store/reducers/generalReducer";

// Workspace Sagas
function* getWorkspaces(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.getWorkspaces, action.params);
    if (response.status === 200) {
      yield put(adminActions.setWorkspaces({
        data: response.data.data || response.data,
        meta: response.data.meta,
      }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.GET_ADMIN_WORKSPACES_ERROR,
      payload: error.response?.data?.message || "Failed to load workspaces",
    });
  }
}

function* grantPackage(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.grantPackage, action.data);
    if (response.status === 200) {
      yield put(adminActions.grantPackageSuccess(response.data.data || response.data));
      yield put(generalActions.successAC({ message: "Package granted successfully", type: "success" }));
      // Refresh workspaces list to show the updated data
      yield call(getWorkspaces, { params: {} });
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.GRANT_PACKAGE_ERROR,
      payload: error.response?.data?.message || "Failed to grant package",
    });
    yield put(generalActions.errorAC({ message: "Failed to grant package", type: "error" }));
  }
}

function* revokePackage(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.revokePackage, action.workspaceId);
    if (response.status === 200) {
      yield put(adminActions.revokePackageSuccess(response.data.data || response.data));
      yield put(generalActions.successAC({ message: "Package revoked successfully", type: "success" }));
      // Refresh workspaces list to show the updated data
      yield call(getWorkspaces, { params: {} });
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.REVOKE_PACKAGE_ERROR,
      payload: error.response?.data?.message || "Failed to revoke package",
    });
    yield put(generalActions.errorAC({ message: "Failed to revoke package", type: "error" }));
  }
}

function* updateWorkspacePackage(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.updateWorkspacePackage, action.data);
    if (response.status === 200) {
      yield call(getWorkspaces, { params: {} }); // Refresh workspaces list
      yield put(generalActions.successAC({ message: "Access dates updated successfully", type: "success" }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.UPDATE_WORKSPACE_PACKAGE_ERROR,
      payload: error.response?.data?.message || "Failed to update access dates",
    });
    throw error; // Re-throw to be caught in component
  }
}

// User Sagas
function* getUsers(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.getUsers, action.params);
    if (response.status === 200) {
      yield put(adminActions.setUsers({
        data: response.data.data || response.data,
        meta: response.data.meta,
      }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.GET_ADMIN_USERS_ERROR,
      payload: error.response?.data?.message || "Failed to load users",
    });
  }
}

function* updateUser(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.updateUser, action.data);
    if (response.status === 200) {
      yield put(adminActions.updateUserSuccess(response.data.data || response.data));
      yield put(generalActions.successAC({ message: "User updated successfully", type: "success" }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.UPDATE_ADMIN_USER_ERROR,
      payload: error.response?.data?.message || "Failed to update user",
    });
    yield put(generalActions.errorAC({ message: "Failed to update user", type: "error" }));
  }
}

function* updateUserWorkspaceRole(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.updateUserWorkspaceRole, action.data);
    if (response.status === 200) {
      yield put(adminActions.updateUserSuccess(response.data.data || response.data));
      yield put(generalActions.successAC({ message: "User role updated successfully", type: "success" }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.UPDATE_ADMIN_USER_ERROR,
      payload: error.response?.data?.message || "Failed to update user role",
    });
    yield put(generalActions.errorAC({ message: "Failed to update user role", type: "error" }));
  }
}

function* deleteUser(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.deleteUser, action.id);
    if (response.status === 200 || response.status === 204) {
      yield put(adminActions.deleteUserSuccess(action.id));
      yield put(generalActions.successAC({ message: "User deleted successfully", type: "success" }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.DELETE_ADMIN_USER_ERROR,
      payload: error.response?.data?.message || "Failed to delete user",
    });
    yield put(generalActions.errorAC({ message: "Failed to delete user", type: "error" }));
  }
}

// Package Sagas
function* getPackages(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.getPackages, action.params);
    if (response.status === 200) {
      yield put(adminActions.setPackages({
        data: response.data.data || response.data,
        meta: response.data.meta,
      }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.GET_ADMIN_PACKAGES_ERROR,
      payload: error.response?.data?.message || "Failed to load packages",
    });
  }
}

function* createPackage(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.createPackage, action.data);
    if (response.status === 200 || response.status === 201) {
      yield put(adminActions.createPackageSuccess(response.data.data || response.data));
      yield put(generalActions.successAC({ message: "Package created successfully", type: "success" }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.CREATE_ADMIN_PACKAGE_ERROR,
      payload: error.response?.data?.message || "Failed to create package",
    });
    yield put(generalActions.errorAC({ message: "Failed to create package", type: "error" }));
  }
}

function* updatePackage(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.updatePackage, action.data);
    if (response.status === 200) {
      yield put(adminActions.updatePackageSuccess(response.data.data || response.data));
      yield put(generalActions.successAC({ message: "Package updated successfully", type: "success" }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.UPDATE_ADMIN_PACKAGE_ERROR,
      payload: error.response?.data?.message || "Failed to update package",
    });
    yield put(generalActions.errorAC({ message: "Failed to update package", type: "error" }));
  }
}

function* deletePackage(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call(adminAPI.deletePackage, action.id);
    if (response.status === 200 || response.status === 204) {
      yield put(adminActions.deletePackageSuccess(action.id));
      yield put(generalActions.successAC({ message: "Package deleted successfully", type: "success" }));
    }
  } catch (error: any) {
    yield put({
      type: adminActionTypes.DELETE_ADMIN_PACKAGE_ERROR,
      payload: error.response?.data?.message || "Failed to delete package",
    });
    yield put(generalActions.errorAC({ message: "Failed to delete package", type: "error" }));
  }
}

// Impersonation Sagas
// Stage 5A B7 (T12.4): start hands out a one-time key; it is traded once for the viewing-as
// token, which this browser then sends (the exchange also marks this browser's session).
export function* impersonateUser(action: any): Generator<any, void, any> {
  try {
    // Store the current admin token before swapping
    const currentToken = localStorage.getItem("token");
    if (currentToken) {
      localStorage.setItem("admin_original_token", currentToken);
    }

    const response: any = yield call(adminAPI.impersonate, action.userId, action.workspaceId, action.emergencyReason);
    if (response.status === 200) {
      const exchanged: any = yield call(adminAPI.exchangeViewAsKey, response.data.data.key);
      const newToken = exchanged.data.data.token;
      localStorage.setItem("token", newToken);
      // Hard redirect forces App.tsx routing to execute exactly as a real login would
      window.location.href = "/";
    }
  } catch (error: any) {
    // Restore the original token if impersonation fails
    const originalToken = localStorage.getItem("admin_original_token");
    if (originalToken) {
      localStorage.setItem("token", originalToken);
      localStorage.removeItem("admin_original_token");
    }
    // A used or expired one-time key (410 VIEW_AS_KEY_USED, stage 5A B7): plain words.
    const keyRefused = error.response?.status === 410 || error.response?.data?.code === "VIEW_AS_KEY_USED";
    yield put(generalActions.errorAC({
      message: keyRefused ? "This View as link has expired. Start View as again." : error.response?.data?.message || "Failed to impersonate user",
      type: "error",
    }));
  }
}

function* stopImpersonation(): Generator<any, void, any> {
  try {
    yield call(authAPI.stopImpersonation);
    // Restore the original admin token
    const originalToken = localStorage.getItem("admin_original_token");
    if (originalToken) {
      localStorage.setItem("token", originalToken);
    }
    localStorage.removeItem("admin_original_token");
    // Hard redirect back to admin users page
    window.location.href = "/admin/users";
  } catch (error: any) {
    // Even on error, attempt to restore the original token and redirect
    const originalToken = localStorage.getItem("admin_original_token");
    if (originalToken) {
      localStorage.setItem("token", originalToken);
    }
    localStorage.removeItem("admin_original_token");
    window.location.href = "/admin/users";
  }
}

export function* adminWatcher() {
  yield all([
    // Workspaces
    takeLatest(adminActionTypes.GET_ADMIN_WORKSPACES, getWorkspaces),
    takeLatest(adminActionTypes.GRANT_PACKAGE, grantPackage),
    takeLatest(adminActionTypes.UPDATE_WORKSPACE_PACKAGE, updateWorkspacePackage),
    takeLatest(adminActionTypes.REVOKE_PACKAGE, revokePackage),
    // Users
    takeLatest(adminActionTypes.GET_ADMIN_USERS, getUsers),
    takeLatest(adminActionTypes.UPDATE_ADMIN_USER, updateUser),
    takeLatest(adminActionTypes.UPDATE_ADMIN_USER_ROLE, updateUserWorkspaceRole),
    takeLatest(adminActionTypes.DELETE_ADMIN_USER, deleteUser),
    // Packages
    takeLatest(adminActionTypes.GET_ADMIN_PACKAGES, getPackages),
    takeLatest(adminActionTypes.CREATE_ADMIN_PACKAGE, createPackage),
    takeLatest(adminActionTypes.UPDATE_ADMIN_PACKAGE, updatePackage),
    takeLatest(adminActionTypes.DELETE_ADMIN_PACKAGE, deletePackage),
    // Impersonation
    takeLatest(adminActionTypes.IMPERSONATE_USER_REQUEST, impersonateUser),
    takeLatest(adminActionTypes.STOP_IMPERSONATION_REQUEST, stopImpersonation),
  ]);
}
